import IPlayerData from '../../Player/IPlayerData';
import { IGameState } from 'State/Game/IGameState';
import { report } from '../../report';
import { DataConflictRestoreSelection } from 'State/Game/DataConflictRestoreSelection';

export enum DataConflictAutoResolveReason {
    EMPTY = 'empty',
    TRANSACTIONS = 'transactions',
    STARS = 'stars',
    DATE = 'date',
}

/**
 * Check for conflicts between two data sets
 */
export function conflicts(a: IPlayerData, b: IPlayerData) {
    // Small optimization, there's no need to parse level data if currencies are different
    if (Number(a.coins) !== Number(b.coins)) return true;

    const levelsA = JSON.parse(a.stars) as number[];
    const levelsB = JSON.parse(b.stars) as number[];
    const levelProgressA = levelsA.length;
    const levelProgressB = levelsB.length;
    const starsA = levelsA.reduce((accumulator: number, currentValue: number) => accumulator + currentValue, 0);
    const starsB = levelsB.reduce((accumulator: number, currentValue: number) => accumulator + currentValue, 0);

    return levelProgressA !== levelProgressB || starsA !== starsB;
}

export function isDataValid(data: IPlayerDataLocation) {
    return data.playerData.coins !== undefined && data.playerData.stars !== undefined;
}

/**
 * Empty data set is return on first time login in Facebook/Firebase.
 */
export function dataSetsEmpty(dataSetA: IPlayerDataLocation, dataSetB: IPlayerDataLocation): boolean {
    let aDataToCompare: boolean = isDataSetEmpty(dataSetA);
    let bDataToCompare: boolean = isDataSetEmpty(dataSetB);

    return aDataToCompare || bDataToCompare;
}

export function isDataSetEmpty(dataSet: IPlayerDataLocation): boolean {
    return !(
        typeof dataSet.playerData === 'object' &&
        dataSet.playerData !== null &&
        'coins' in dataSet.playerData &&
        'stars' in dataSet.playerData &&
        parseStars(dataSet, false).length >= 1
    );
}

/**
 *
 */
export enum PlayerDataLocation {
    DEVICE = 'device',
    CLOUD = 'cloud',
    FACEBOOK = 'facebook',
    GAME_BACKEND = 'gameBackend',
    LOST_DATA_RESTORE = 'lostDataRestore',
}

/**
 * Data location could is either (device/cloud)
 */
export interface IPlayerDataLocation {
    location: PlayerDataLocation;
    playerData: IPlayerData;
    autoResolveReason?: DataConflictAutoResolveReason;
}

function parseStars(set: IPlayerDataLocation, filterNull: boolean = true): number[] {
    const stars = set.playerData?.stars ? JSON.parse(set.playerData?.stars) : [];
    return filterNull ? stars.filter((starCount: number) => starCount !== null) : stars;
}

/**
 * Sort the data sets so the data set with the highest progress is the first item in the array.
 */
export function sortDataLocationSets(sets: IPlayerDataLocation[]): IPlayerDataLocation[] {
    return sets.sort((setA, setB) => {
        const starsA = parseStars(setA);
        const starsB = parseStars(setB);
        const coinsA = setA.playerData?.coins ?? 0;
        const coinsB = setB.playerData?.coins ?? 0;
        return starsB.length - starsA.length || sumArray(starsB) - sumArray(starsA) || coinsB - coinsA;
    });
}

function sumArray(array: number[]) {
    return array.reduce((a: number, b: number) => a + b, 0);
}

export function hasValidLevelData(dataSet: IPlayerDataLocation) {
    return (
        typeof dataSet === 'object' &&
        dataSet.playerData !== null &&
        typeof dataSet.playerData === 'object' &&
        'stars' in dataSet.playerData &&
        parseStars(dataSet).length >= 0
    );
}

export function hasValidFacebookData(state: IGameState, dataSets: IPlayerDataLocation[]): boolean {
    const facebookData = dataSets.filter(data => data.location === PlayerDataLocation.FACEBOOK)[0];

    return hasValidLevelData(facebookData);
}

export function getHighestLevelCompleted(playerData: IPlayerDataLocation): number {
    try {
        if (!hasValidLevelData(playerData)) return 0;

        const stars = parseStars(playerData);

        return stars.filter(starCount => starCount !== null).length;
    } catch (e) {
        report(e.message);
    }
}

export function autoResolveDataSetsIfPossible(sets: IPlayerDataLocation[]): IPlayerDataLocation[] {
    return sets.reduce((remainingSets: IPlayerDataLocation[], setA: IPlayerDataLocation): IPlayerDataLocation[] => {
        if (remainingSets.length === 0) {
            remainingSets.push(setA);
            return remainingSets;
        }
        let autoResolved: boolean = false;
        remainingSets = remainingSets.map((setB: IPlayerDataLocation) => {
            // If setA has data
            if (!isDataSetEmpty(setA)) {
                // And setB is empty then choose setA
                if (isDataSetEmpty(setB)) {
                    return setAutoResolved(setA, DataConflictAutoResolveReason.EMPTY);
                }

                // Check if completed level counts are greater than 1
                if (getLevelCountDifference(setA, setB) > 1) {
                    // If so conflict must be resolved manually
                    return setB;
                }

                // Check if purchases are equal
                if (!arePurchasesEqual(setA, setB)) {
                    // If counts are not equal, choose the set with the higher count
                    const resolvedSet = getPurchases(setA) > getPurchases(setB) ? setA : setB;

                    return setAutoResolved(resolvedSet, DataConflictAutoResolveReason.TRANSACTIONS);
                }

                // Check if star counts are equal
                if (!areStarCountsEqual(setA, setB)) {
                    // If counts are not equal, choose the set with the higher count
                    const resolvedSet = getStarCount(setA) > getStarCount(setB) ? setA : setB;
                    return setAutoResolved(resolvedSet, DataConflictAutoResolveReason.STARS);
                }

                const resolvedSet = getMostRecentSet(setA, setB);

                return setAutoResolved(resolvedSet, DataConflictAutoResolveReason.DATE);

                // If setA is empty check if setB is also empty
            } else if (!isDataSetEmpty(setB)) {
                // If setB is not empty then resolve with setB
                return setAutoResolved(setB, DataConflictAutoResolveReason.EMPTY);
                // If both sets are empty auto resolve with either set
            } else {
                return setAutoResolved(setA, DataConflictAutoResolveReason.EMPTY);
            }

            // Not able to auto resolve, return without changes
            return setB;

            function setAutoResolved(
                set: IPlayerDataLocation,
                reason: DataConflictAutoResolveReason
            ): IPlayerDataLocation {
                set.autoResolveReason = reason;
                autoResolved = true;
                return set;
            }

            function getLevelCountSafely(set: IPlayerDataLocation): number {
                if (hasValidLevelData(set)) {
                    return getHighestLevelCompleted(set);
                }
                return 0;
            }

            function getLevelCountDifference(setA: IPlayerDataLocation, setB: IPlayerDataLocation): number {
                return Math.abs(getLevelCountSafely(setA) - getLevelCountSafely(setB));
            }

            function getStarCount(set: IPlayerDataLocation): number {
                return sumArray(parseStars(set));
            }

            function areStarCountsEqual(setA: IPlayerDataLocation, setB: IPlayerDataLocation): boolean {
                return getStarCount(setA) === getStarCount(setB);
            }

            function getPurchases(set: IPlayerDataLocation): number {
                return Number(set?.playerData?.purchases ?? 0);
            }

            function arePurchasesEqual(setA: IPlayerDataLocation, setB: IPlayerDataLocation): boolean {
                return getPurchases(setA) === getPurchases(setB);
            }

            function getMostRecentSet(setA: IPlayerDataLocation, setB: IPlayerDataLocation): IPlayerDataLocation {
                const setADate = new Date(setA?.playerData?.data_timestamp ?? 0);
                const setBDate = new Date(setB?.playerData?.data_timestamp ?? 0);
                return setADate.getTime() > setBDate.getTime() ? setA : setB;
            }
        });

        if (!autoResolved) {
            remainingSets.push(setA);
        }

        return remainingSets;
    }, []);
}

export function updateLostDataPropertyInChosenSet(
    sets: IPlayerDataLocation[],
    chosenSet: IPlayerDataLocation
): IPlayerDataLocation {
    const setsContainLostDataSet = sets.some(set => set.location === PlayerDataLocation.LOST_DATA_RESTORE);
    if (setsContainLostDataSet) {
        return {
            ...chosenSet,
            playerData: {
                ...chosenSet.playerData,
                __lostData: null,
            },
        };
    }

    return {
        ...chosenSet,
        playerData: {
            ...chosenSet.playerData,
            __lostData: sets[0]?.playerData?.__lostData ?? sets[1]?.playerData?.__lostData,
        },
    };
}

export function getFacebookData(dataSets: IPlayerDataLocation[]): IPlayerDataLocation {
    return getPlayerDataByLocation(dataSets, PlayerDataLocation.FACEBOOK);
}

export function getPlayerDataByLocation(
    dataSets: IPlayerDataLocation[],
    location: PlayerDataLocation
): IPlayerDataLocation {
    return dataSets.find(dataSet => dataSet.location === location);
}

export function getRestoreSelection(dataSets: IPlayerDataLocation[], resolvedSet: IPlayerDataLocation) {
    const otherDataSet = dataSets.find(set => set.location !== resolvedSet.location);
    const isOlderDataSet = resolvedSet.playerData?.data_timestamp > otherDataSet.playerData?.data_timestamp;
    return isOlderDataSet ? DataConflictRestoreSelection.OLD : DataConflictRestoreSelection.NEW;
}
